import { useApolloClient, useMutation, useQuery } from '@apollo/client/react'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { useAuth } from '@/auth/useAuth'
import {
  MarkAsReadNotificationDocument,
  MarkAsReadNotificationMutation,
  MarkAsReadNotificationMutationVariables,
  NotificationsDocument,
  NotificationsQuery,
  NotificationsQueryVariables,
} from '@/graphql/api/generated'
import { Link } from '@/routing'
import { buildNotificationLink } from '@/utils/notificationLink'
import { refetchCommunityShowViewQuery } from '@/utils/refetchCommunityShowViewQuery'
import { refetchMaterialBookingQueries } from '@/utils/refetchMaterialBookingQueries'

import {
  type NotificationPayload,
  useMercureNotifications,
} from './useMercureNotifications'
import { useMercureSubscribeToken } from './useMercureSubscribeToken'

/** Notification types with HTML in notifications.json (same rich rendering as NotificationItem). */
const NOTIFICATION_TOAST_RICH_HTML_TYPES = new Set([
  'material_option_low_stock',
  'material_option_projected_shortage',
  'material_option_restock_week_reminder',
])

type NotificationNode = NonNullable<
  NonNullable<NotificationsQuery['notifications']>['edges']
>[number]['node']

export type NotificationsReadFilter = 'unread' | 'read' | undefined

export interface NotificationsPageInfo {
  hasNextPage: boolean
  hasPreviousPage: boolean
  startCursor?: string | null
  endCursor?: string | null
}

export interface UseNotificationsOptions {
  /** Server-side filter: "unread" | "read" | undefined (all) */
  read?: NotificationsReadFilter
  /** Whether to show toast notifications for new notifications. Default: true */
  showToast?: boolean
  /** How many notifications to load (most recent first). Default: 30 */
  first?: number
  /** Cursor pagination (used by the dedicated page pager). */
  afterCursor?: string
  beforeCursor?: string
}

export interface UseNotificationsResult {
  notifications: NotificationNode[]
  unreadCount: number
  totalCount: number
  pageInfo: NotificationsPageInfo | undefined
  markAsRead: (ids: string[], read?: boolean) => Promise<void>
  markAsReadLoading: boolean
  loading: boolean
  error: Error | null
  currentUserId: string | undefined
}

export function isNotificationUnread(
  node:
    | {
        readAt?: string | null
        readByUsers?: { edges?: Array<{ node?: { id: string } | null }> }
      }
    | null
    | undefined,
  currentUserId: string | undefined
): boolean {
  if (!node) return false
  if (node.readAt != null && node.readAt !== '') return false
  const readByUserIds =
    node.readByUsers?.edges?.map((e) => e?.node?.id).filter(Boolean) ?? []
  if (readByUserIds.length > 0 && currentUserId) {
    return !readByUserIds.includes(currentUserId)
  }
  return true
}

function isUnread(
  node: NotificationNode | null | undefined,
  currentUserId: string | undefined
): boolean {
  return isNotificationUnread(node, currentUserId)
}

export function useNotifications(
  options?: UseNotificationsOptions
): UseNotificationsResult {
  const { t } = useTranslation('notifications')
  const { user, token } = useAuth()
  const client = useApolloClient()

  // Get user gender for context-aware translations
  const genderContext =
    user?.gender === 'male'
      ? 'male'
      : user?.gender === 'female'
        ? 'female'
        : user?.gender === 'nonBinary'
          ? 'nonBinary'
          : undefined
  const { token: mercureToken, topics: mercureTopics } =
    useMercureSubscribeToken(token)
  const notificationTopics = useMemo(
    () =>
      mercureTopics.filter(
        (topic) =>
          topic.endsWith('/announcements') || topic.endsWith('/notifications')
      ),
    [mercureTopics]
  )
  const readFilter = options?.read
  const first = options?.first ?? 30
  const afterCursor = options?.afterCursor
  const beforeCursor = options?.beforeCursor
  const {
    data,
    loading: queryLoading,
    error: queryError,
    refetch,
  } = useQuery<NotificationsQuery, NotificationsQueryVariables>(
    NotificationsDocument,
    {
      variables: {
        first,
        ...(afterCursor != null && { afterCursor }),
        ...(beforeCursor != null && { beforeCursor }),
        ...(readFilter != null && { read: readFilter }),
      },
      skip: !user,
      notifyOnNetworkStatusChange: true,
    }
  )

  const [markAsReadMutation, { loading: markAsReadLoading }] = useMutation<
    MarkAsReadNotificationMutation,
    MarkAsReadNotificationMutationVariables
  >(MarkAsReadNotificationDocument)

  // Track recently modified notifications to avoid showing toasts for user-initiated updates
  const recentlyModifiedRef = useRef<Set<string>>(new Set())
  const recentlyModifiedTimeoutsRef = useRef<
    Map<string, ReturnType<typeof setTimeout>>
  >(new Map())

  const onNotification = useCallback(
    (n: NotificationPayload) => {
      // Context synchronization events are consumed by useMercureContextUpdates.
      // They are not user-facing notifications and must not create toasts.
      if (n.type === 'context_switched') {
        return
      }

      // Check if this notification already exists in the current data
      // If it exists, it's likely an update (e.g., mark as read/unread) rather than a new notification
      const existingNotification = data?.notifications?.edges?.find(
        (edge) => edge?.node?.id === n.id
      )

      // Check if this notification was recently modified by the user
      // If notification has no ID, check if it matches an existing one by type and context
      let isRecentlyModified = false
      let matchedNotificationId: string | undefined

      if (n.id) {
        isRecentlyModified = recentlyModifiedRef.current.has(n.id)
        matchedNotificationId = n.id
      } else if (existingNotification) {
        // If no ID but exists in list, it's likely an update
        isRecentlyModified = true
        matchedNotificationId = existingNotification.node?.id
      } else if (n.context) {
        // Try to match by context (bookingId or materialId) and type if no ID
        const matchingNotification = data?.notifications?.edges?.find(
          (edge) => {
            const node = edge?.node
            if (!node || node.type !== n.type) return false
            const nodeCtx =
              typeof node.context === 'string'
                ? JSON.parse(node.context)
                : (node.context as Record<string, string> | undefined)
            return (
              (n.context?.bookingId &&
                nodeCtx?.bookingId === n.context.bookingId) ||
              (n.context?.materialId &&
                nodeCtx?.materialId === n.context.materialId)
            )
          }
        )
        if (matchingNotification?.node?.id) {
          matchedNotificationId = matchingNotification.node.id
          isRecentlyModified = recentlyModifiedRef.current.has(
            matchedNotificationId
          )
        }
      }

      // If notification already exists in the list, it's an update (mark as read/unread)
      // We don't check readAt because when marking as unread, readAt becomes null
      const isUpdate = !!existingNotification || isRecentlyModified
      const isNewNotification = !isUpdate

      refetch()
      // Refetch booking queries when notification concerns a booking (e.g. new message, status change).
      // If user is on that booking page, messages/status will update; otherwise cache is updated.
      if (n.context?.bookingId) {
        refetchMaterialBookingQueries(client, n.context).catch(() => {})
      }
      // Refetch community show when user receives join accepted/rejected while on the community page.
      const isCircleJoinOutcome =
        n.type === 'circle_join_accepted' ||
        n.type === 'circle_join_rejected' ||
        n.type === 'circle_membership_accepted'
      if (isCircleJoinOutcome && n.context?.circleId) {
        refetchCommunityShowViewQuery(client, n.context).catch(() => {})
      }
      if (options?.showToast !== false && isNewNotification) {
        // For announcements, title and message are directly in params
        // For other types, build translation keys from notification type: e.g., "circle_membership_accepted.title"
        const isAnnouncementType = n.type === 'announcement' || n.type === 'raw'
        const hasDirectParams =
          isAnnouncementType &&
          n.params &&
          typeof n.params === 'object' &&
          'title' in n.params &&
          'message' in n.params

        let displayTitle: string
        let displayMessage: string

        if (hasDirectParams && n.params) {
          // For announcements, use params.title and params.message directly
          displayTitle =
            (n.params.title as string) || n.title || t('unknown.title')
          displayMessage =
            (n.params.message as string) || n.message || t('unknown.message')
        } else if (n.type) {
          // For other types, use translation keys built from type
          const titleKey = `${n.type}.title`
          const messageKey = `${n.type}.message`

          displayTitle = t(titleKey, {
            ...(n.params ?? {}),
            ...(genderContext ? { context: genderContext } : {}),
            defaultValue: n.title ?? t('unknown.title'),
          })
          displayMessage = t(messageKey, {
            ...(n.params ?? {}),
            ...(genderContext ? { context: genderContext } : {}),
            defaultValue: n.message ?? t('unknown.message'),
          })
        } else {
          // Fallback to legacy title/message
          displayTitle = n.title ?? t('unknown.title')
          displayMessage = n.message ?? t('unknown.message')
        }
        // For announcements, only show a short excerpt in the toast
        const toastMessageExcerptLength = 60
        const toastMessage = isAnnouncementType
          ? displayMessage.length > toastMessageExcerptLength
            ? `${displayMessage.slice(0, toastMessageExcerptLength).trim()}…`
            : displayMessage
          : displayMessage
        const useRichToastHtml =
          !hasDirectParams &&
          Boolean(n.type) &&
          NOTIFICATION_TOAST_RICH_HTML_TYPES.has(n.type)
        const text = toastMessage
          ? `${displayTitle} — ${toastMessage}`
          : displayTitle

        // Link destination + carried scope (single source of truth, shared with the list item).
        const linkTarget = buildNotificationLink(n)
        const linkRoute: { to: string } | null = linkTarget
          ? { to: linkTarget }
          : null
        const ToastContent = () => {
          const buttonText = t('actions.view', {
            defaultValue: 'Voir',
          })
          const primaryLine = useRichToastHtml
            ? React.createElement(
                React.Fragment,
                null,
                displayTitle,
                ' — ',
                React.createElement(Trans, {
                  t,
                  tOptions: genderContext
                    ? { context: genderContext }
                    : undefined,
                  i18nKey: `${n.type}.message`,
                  ns: 'notifications',
                  values: {
                    ...(typeof n.params === 'object' && n.params !== null
                      ? (n.params as Record<string, unknown>)
                      : {}),
                  },
                  components: {
                    strong: React.createElement('strong', {
                      style: { fontWeight: 600 },
                    }),
                    em: React.createElement('em', { fontStyle: 'italic' }),
                  },
                })
              )
            : text
          return React.createElement(
            'div',
            null,
            React.createElement(
              'div',
              {
                style: {
                  marginBottom: linkRoute ? '8px' : '0',
                },
              },
              primaryLine
            ),
            linkRoute &&
              React.createElement(
                Link,
                {
                  to: linkRoute.to,
                  style: {
                    display: 'inline-block',
                    marginTop: '8px',
                    padding: '6px 12px',
                    backgroundColor: '#3182ce',
                    color: 'white',
                    borderRadius: '4px',
                    textDecoration: 'none',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                  },
                  onMouseEnter: (e: React.MouseEvent<HTMLAnchorElement>) => {
                    e.currentTarget.style.backgroundColor = '#2c5aa0'
                  },
                  onMouseLeave: (e: React.MouseEvent<HTMLAnchorElement>) => {
                    e.currentTarget.style.backgroundColor = '#3182ce'
                  },
                  onClick: () => {
                    // Mark as read when clicking the button
                    if (n.id) {
                      markAsReadMutation({
                        variables: { ids: [n.id], read: true },
                      }).catch(() => {
                        // Ignore errors
                      })
                    }
                    // Navigation and context switching are handled by RedirectView
                  },
                },
                buttonText
              )
          )
        }

        toast.info(React.createElement(ToastContent), { autoClose: 6000 })
      }
    },
    [
      client,
      refetch,
      t,
      options?.showToast,
      markAsReadMutation,
      data?.notifications?.edges,
      genderContext,
    ]
  )

  useMercureNotifications({
    topics: notificationTopics,
    mercureToken,
    onNotification,
    enabled: !!user && !!mercureToken,
  })

  useEffect(() => {
    const onFocus = () => refetch()
    document.addEventListener('visibilitychange', onFocus)
    return () => {
      document.removeEventListener('visibilitychange', onFocus)
      // Clean up timeouts on unmount
      recentlyModifiedTimeoutsRef.current.forEach((timeout) =>
        clearTimeout(timeout)
      )
      recentlyModifiedTimeoutsRef.current.clear()
      recentlyModifiedRef.current.clear()
    }
  }, [refetch])

  const markAsRead = useCallback(
    async (ids: string[], read?: boolean) => {
      if (ids.length === 0) return

      // Track these notifications as recently modified to avoid showing toasts
      ids.forEach((id) => {
        recentlyModifiedRef.current.add(id)
        // Clear any existing timeout for this ID
        const existingTimeout = recentlyModifiedTimeoutsRef.current.get(id)
        if (existingTimeout) {
          clearTimeout(existingTimeout)
        }
        // Remove from tracking after 5 seconds
        const timeout = setTimeout(() => {
          recentlyModifiedRef.current.delete(id)
          recentlyModifiedTimeoutsRef.current.delete(id)
        }, 5000)
        recentlyModifiedTimeoutsRef.current.set(id, timeout)
      })

      await markAsReadMutation({
        variables: { ids, read: read === false ? false : true },
      })
      refetch()
    },
    [markAsReadMutation, refetch]
  )

  const notifications = useMemo(() => {
    const nodes =
      data?.notifications?.edges?.map((e) => e?.node).filter(Boolean) ??
      ([] as NotificationNode[])
    return [...nodes].sort(
      (a, b) =>
        new Date(b?.createdAt ?? 0).getTime() -
        new Date(a?.createdAt ?? 0).getTime()
    )
  }, [data?.notifications?.edges])

  const unreadCount = useMemo(() => {
    if (readFilter === 'read') return 0
    if (readFilter === 'unread') return data?.notifications?.totalCount ?? 0
    return notifications.filter((n) => isUnread(n, user?.id)).length
  }, [readFilter, data?.notifications?.totalCount, notifications, user?.id])

  return {
    notifications,
    unreadCount,
    totalCount: data?.notifications?.totalCount ?? 0,
    pageInfo: data?.notifications?.pageInfo,
    markAsRead,
    markAsReadLoading,
    loading: queryLoading,
    error: queryError ?? null,
    currentUserId: user?.id,
  }
}
